import React, { useState } from "react";
import {
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import { FilterGroup, FilterRule, SchemaColumn } from "./types";
import { useReportBuilderStore } from "./store";
import {
  DATA_TYPE_CONFIG,
  getDefaultOperator,
  getOperatorLabels,
} from "./constants";

// ─── Filter rule row ──────────────────────────────────────────────────────────

function FilterRuleRow({
  rule,
  groupId,
  isLast,
  groupLogic,
  filterableColumns,
}: {
  rule: FilterRule;
  groupId: string;
  isLast: boolean;
  groupLogic: "AND" | "OR";
  filterableColumns: SchemaColumn[];
}) {
  const { config, activeSheetId, updateFilterRule, removeFilterRule } =
    useReportBuilderStore();
  const activeSheet =
    config.sheets.find((sheet) => sheet.id === activeSheetId) ??
    config.sheets[0];

  const operatorLabels = getOperatorLabels(rule.dataType);
  const needsValue = !["is_blank", "is_true", "is_false"].includes(
    rule.operator
  );
  const isBetween =
    rule.operator === "between" || rule.operator === "is_between";
  const isInLast = rule.operator === "is_in_last";

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-1.5">
        {/* Column selector */}
        <select
          value={rule.column}
          onChange={(e) => {
            const col = filterableColumns.find((c) => c.id === e.target.value);
            if (!col) return;
            updateFilterRule(groupId, rule.id, {
              column: col.id,
              sourceColumn: col.name,
              sourceTable: col.sourceTable,
              columnDisplayName: col.displayName,
              dataType: col.dataType,
              operator: getDefaultOperator(
                col.dataType
              ) as FilterRule["operator"],
              value: null,
              valueTo: null,
            });
          }}
          className="flex-1 min-w-0 text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary bg-white"
        >
          <option value="">Select column…</option>
          {filterableColumns.map((col) => (
            <option key={col.id} value={col.id}>
              {col.displayName}
            </option>
          ))}
        </select>

        {/* Operator selector */}
        {rule.column && (
          <select
            value={rule.operator}
            onChange={(e) =>
              updateFilterRule(groupId, rule.id, {
                operator: e.target.value as FilterRule["operator"],
                value: null,
                valueTo: null,
              })
            }
            className="flex-1 min-w-0 text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary bg-white"
          >
            {Object.entries(operatorLabels).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        )}

        {/* Remove */}
        <button
          onClick={() => removeFilterRule(groupId, rule.id)}
          className="flex-shrink-0 p-1.5 text-gray-300 hover:text-red-400 transition-colors"
          title="Remove filter"
        >
          <TrashIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Value input(s) */}
      {rule.column && needsValue && (
        <div className="flex items-center gap-1.5 pl-0.5">
          <FilterValueInput
            rule={rule}
            groupId={groupId}
            fieldKey="value"
            placeholder={isBetween ? "From" : isInLast ? "Number" : "Value"}
          />
          {isBetween && (
            <>
              <span className="text-xs text-gray-400">and</span>
              <FilterValueInput
                rule={rule}
                groupId={groupId}
                fieldKey="valueTo"
                placeholder="To"
              />
            </>
          )}
          {isInLast && (
            <select
              value={rule.unit ?? "days"}
              onChange={(e) =>
                updateFilterRule(groupId, rule.id, { unit: e.target.value })
              }
              className="text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary bg-white"
            >
              <option value="days">days</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
            </select>
          )}
          {/* Dynamic value shortcuts */}
          {rule.dataType === "date" && (
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) {
                  const relativeDateShortcuts: Record<
                    string,
                    { value: number; unit: string }
                  > = {
                    __last_7_days: { value: 7, unit: "days" },
                    __last_30_days: { value: 30, unit: "days" },
                  };
                  const shortcut = relativeDateShortcuts[e.target.value];
                  updateFilterRule(groupId, rule.id, {
                    ...(shortcut
                      ? {
                          operator: "is_in_last" as FilterRule["operator"],
                          value: shortcut.value,
                          unit: shortcut.unit,
                        }
                      : { value: e.target.value }),
                  });
                }
              }}
              className="text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary bg-white"
              title="Quick date shortcuts"
            >
              <option value="">Quick…</option>
              <option value="{{today}}">Today</option>
              <option value="{{current_month_start}}">Month start</option>
              <option value="{{current_year_start}}">Year start</option>
              <option value="__last_7_days">Last 7 days</option>
              <option value="__last_30_days">Last 30 days</option>
            </select>
          )}
        </div>
      )}

      {/* AND/OR connector between rules */}
      {!isLast && (
        <div className="flex items-center gap-1.5 py-0.5 pl-1">
          <div className="h-px flex-1 bg-gray-100" />
          <span className="text-[10px] font-semibold text-primary bg-activeColor border border-activeColor px-1.5 py-0.5 rounded">
            {groupLogic}
          </span>
          <div className="h-px flex-1 bg-gray-100" />
        </div>
      )}
    </div>
  );
}

// ─── Value input ──────────────────────────────────────────────────────────────

function FilterValueInput({
  rule,
  groupId,
  fieldKey,
  placeholder,
}: {
  rule: FilterRule;
  groupId: string;
  fieldKey: "value" | "valueTo";
  placeholder?: string;
}) {
  const { schema, updateFilterRule } = useReportBuilderStore();
  const val = rule[fieldKey];
  const selectedColumn = schema
    .flatMap((table) => table.columns)
    .find((column) => column.id === rule.column);
  const suggestedValues = selectedColumn?.exampleValues ?? [];
  const listId = `filter-values-${rule.id}-${fieldKey}`;

  const isNumeric = ["number", "currency", "percentage"].includes(
    rule.dataType
  );
  const isDate = rule.dataType === "date";

  return (
    <>
      <input
        type={isNumeric ? "number" : isDate ? "date" : "text"}
        list={suggestedValues.length > 0 ? listId : undefined}
        value={val === null || val === undefined ? "" : String(val)}
        onChange={(e) =>
          updateFilterRule(groupId, rule.id, {
            [fieldKey]: isNumeric
              ? e.target.value
                ? Number(e.target.value)
                : null
              : e.target.value || null,
          })
        }
        placeholder={placeholder ?? "Value"}
        className="flex-1 min-w-0 text-xs px-2.5 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
      />
      {suggestedValues.length > 0 && (
        <datalist id={listId}>
          {suggestedValues.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
      )}
    </>
  );
}

// ─── Filter group ─────────────────────────────────────────────────────────────

function FilterGroupBlock({
  group,
  filterableColumns,
}: {
  group: FilterGroup;
  filterableColumns: SchemaColumn[];
}) {
  const { removeFilterGroup, setFilterGroupLogic, addFilterRule } =
    useReportBuilderStore();

  function handleAddRule() {
    const firstCol = filterableColumns[0];
    if (!firstCol) return;
    addFilterRule(group.id, {
      column: firstCol.id,
      sourceColumn: firstCol.name,
      sourceTable: firstCol.sourceTable,
      columnDisplayName: firstCol.displayName,
      dataType: firstCol.dataType,
      operator: getDefaultOperator(firstCol.dataType) as FilterRule["operator"],
      value: null,
    });
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-3">
      {/* Group header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
        {/* AND / OR toggle */}
        <div className="flex items-center rounded-md border border-gray-200 overflow-hidden text-[11px] font-semibold">
          {(["AND", "OR"] as const).map((logic) => (
            <button
              key={logic}
              onClick={() => setFilterGroupLogic(group.id, logic)}
              className={`px-2.5 py-1 transition-colors ${
                group.logic === logic
                  ? "bg-primary text-white"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              {logic}
            </button>
          ))}
        </div>
        <span className="flex-1 text-[11px] text-gray-500">
          All {group.logic === "AND" ? "of these" : "any of these"} rules must
          match
        </span>
        <button
          onClick={() => removeFilterGroup(group.id)}
          className="text-gray-300 hover:text-red-400 transition-colors"
          title="Remove filter group"
        >
          <TrashIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Rules */}
      <div className="px-3 py-3 flex flex-col gap-0">
        {group.rules.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-3">
            No rules yet — add one below
          </p>
        ) : (
          group.rules.map((rule, i) => (
            <FilterRuleRow
              key={rule.id}
              rule={rule}
              groupId={group.id}
              isLast={i === group.rules.length - 1}
              groupLogic={group.logic}
              filterableColumns={filterableColumns}
            />
          ))
        )}

        <button
          onClick={handleAddRule}
          disabled={filterableColumns.length === 0}
          className="mt-2 flex items-center gap-1.5 text-xs text-primary hover:text-indigo-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          Add rule
        </button>
      </div>
    </div>
  );
}

// ─── Filters panel ────────────────────────────────────────────────────────────

export function FiltersPanel() {
  const {
    config,
    schema,
    activeSheetId,
    addFilterGroup,
    removeFilterGroup,
    isFiltersPanelOpen,
    toggleFiltersPanel,
    isAdvancedMode,
  } = useReportBuilderStore();

  // Filters are an advanced concept — hide entirely in simple mode
  if (!isAdvancedMode) return null;
  const activeSheet =
    config.sheets.find((s) => s.id === activeSheetId) ?? config.sheets[0];

  const totalRules = activeSheet.filters.reduce(
    (sum, g) => sum + g.rules.length,
    0
  );
  // Filters are intentionally limited to output fields. This keeps the
  // resulting report explainable: every field used to include or exclude a
  // row is visible to the person reading it.
  const filterableColumns = activeSheet.columns
    .filter(
      (column) => !column.isCustom && column.sourceTable && column.sourceColumn
    )
    .map((column) => {
      const schemaColumn = schema
        .flatMap((table) => table.columns)
        .find(
          (item) =>
            item.sourceTable === column.sourceTable &&
            item.name === column.sourceColumn
        );
      return schemaColumn
        ? { ...schemaColumn, id: column.id }
        : {
            id: column.id,
            name: column.sourceColumn!,
            displayName: column.displayName,
            dataType: column.type,
            description: column.description ?? column.displayName,
            sourceTable: column.sourceTable!,
            isCustom: false,
          };
    });

  return (
    <div className="bg-white border-b border-gray-200">
      {/* Panel toggle header */}
      <button
        onClick={toggleFiltersPanel}
        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
      >
        <FunnelIcon className="w-4 h-4 text-gray-500" />
        <span className="text-xs font-semibold text-gray-700">Filters</span>
        {totalRules > 0 && (
          <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-activeColor text-secondary rounded-full">
            {totalRules}
          </span>
        )}
        <div className="flex-1" />
        {isFiltersPanelOpen ? (
          <ChevronUpIcon className="w-3.5 h-3.5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
        )}
      </button>

      {/* Expanded filters content */}
      {isFiltersPanelOpen && (
        <div className="px-4 pb-3">
          {filterableColumns.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-3">
              Add a report field before creating filters
            </p>
          ) : (
            <>
              {activeSheet.filters.map((group) => (
                <FilterGroupBlock
                  key={group.id}
                  group={group}
                  filterableColumns={filterableColumns}
                />
              ))}

              <div className="flex items-center justify-between gap-3 pt-1">
                <button
                  onClick={addFilterGroup}
                  className="flex items-center gap-1.5 text-xs text-primary hover:text-indigo-800 transition-colors"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Add filter group
                </button>
                {totalRules > 0 && (
                  <button
                    onClick={() => {
                      activeSheet.filters.forEach((group) =>
                        removeFilterGroup(group.id)
                      );
                    }}
                    className="text-xs text-gray-500 hover:text-red-500 transition-colors"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
